// @flow
import React from 'react';
import * as _ from "lodash";
import { connect } from "react-redux";
import { withRouter } from 'react-router-dom';

import type { TCoachPlayerStatsById } from '../../modules/types';
import type { TPlayerWithFixture } from '../../modules/types/player';
import { coach_player_stats } from '../../helpers/stats';
import {
	OneColumnLayout,
	TopBar,
	AdsContainer,
	Ad,
	PlayersOnTrade,
	TeamForTrade,
	PlayersTable,
	TeamToTeamTrade
} from '../../components';
import JsonFetcher from '../../components/utils/JsonFetcher';
import * as selectors from "../../modules/selectors";
import type {
	TCoachStatsListReducer,
	TPlayerStatusesReducer, TPosition, TRootStore,
	TSquad,
	TTeamsDraftTradesReducer,
} from "../../modules/types";
import * as actions from "../../modules/actions";

type TableColumns = Array<{ key: string, prop: string, prop_sub?: string, tooltip?: string }>;

type Props = {
	match: Object,
	league: Object,
	teams_by_id: Object,
	players: Array<TPlayerWithFixture>,
	showDraftLeague: typeof actions.leagueDraft.showDraftLeague,
	fetchTeams: typeof actions.leagueDraft.showTeamsDraftLeague,
	fetchRounds: typeof actions.rounds.fetchRounds,
	fetchWaiverList: typeof actions.leagueDraft.fetchWaiverList,
	fetchWaiverFree: typeof actions.leagueDraft.fetchWaiverFree,
	fetchTeam: typeof actions.teamsDraft.fetchTeam,
	is_pending_players: boolean,
	positions: Array<TPosition>,
	squads: Array<TSquad>,
	playerStatuses: TPlayerStatusesReducer,
	actual_round: Object,
	table: {
		basic: TableColumns,
		coach: Object,
	},
	coach_stats_list: TCoachStatsListReducer,
	has_assistant_coach: boolean,
	coach_players_by_id: TCoachPlayerStatsById,
	waiver_requests: string,
	user_team: Object,
}

type TFilters = {
	search: string,
	by_squad: string,
	by_position: string,
	by_status: string,
	by_dpp: boolean,
	by_favourites: boolean,
	by_drafted: boolean,
	by_fa: boolean
}

type State = {
	order_by_desc: boolean,
	filters: TFilters,
	sort_by?: string,
	order_by_desc?: boolean,
	players_show_limit?: number,
	players_show_limit_step?: number,
	player_row_menu_ids: Array<number>,
	is_coach_active: boolean,
	coach_type: string,
	fa_trade: {
		new_player_id: number,
		old_player_id: number
	},
	team_trade: TTeamsDraftTradesReducer,
	is_fa_trade: boolean,
	is_team_trade: boolean,
	show_team: boolean
};

class PlayersComponent extends React.Component<Props, State> {

	state = {
		players_show_limit: 20,
		players_show_limit_step: 20,
		sort_by: 'stats.proj_avg',
		order_by_desc: true,
		player_row_menu_ids: [],
		filters: {
			search: '',
			by_squad: '',
			by_position: '',
			by_status: '',
			by_dpp: false,
			by_favourites: false,
			by_drafted: false,
			by_fa: false
		},
		is_coach_active: false,
		coach_type: 'coaches-choice',
		fa_trade: {
			new_player_id: 0,
			old_player_id: 0
		},
		team_trade: {
			new_player_id: [],
			old_player_id: [],
			owner_id: 0
		},
		is_fa_trade: false,
		is_team_trade: false,
		show_team: false
	};

	static defaultProps = {
		players: [],
		actual_round: {},
		table: {
			basic: [
				{ key: 'AVG', prop: 'stats.avg_points', tooltip: 'Average Points' },
				{ key: 'TP', prop: 'stats.total_points', tooltip: 'Total Points' },
				{ key: 'RD X Score', prop: '' },
				{ key: 'L3 AVG', prop: 'stats.last_3_avg', tooltip: 'Last 3 Average' },
			],
			coach: coach_player_stats,
			pre_draft_list_size: 0,
		},
		waiver_requests: ''
	};

	ref = React.createRef();

	constructor(props, state) {
		super(props, state);

		_.bindAll(this, [
			'addOutTrade',
			'addInTrade',
			'cancelTradeRequest',
			'removeOutPlayer',
			'removeInPlayer',
			'reFetchData',
		]);
	}

	componentDidMount() {
		const {
			showDraftLeague,
			fetchTeams,
			fetchRounds,
			fetchWaiverList,
			fetchWaiverFree,
			match: { params: { league_id } },
			fetchTeam
		} = this.props;

		showDraftLeague({ id: league_id });
		fetchTeams({
			league_id: league_id,
			details: 1
		});

		fetchRounds();

		fetchWaiverList({
			league_id
		});
		fetchWaiverFree({
			league_id
		});

		fetchTeam({
			league_id
		});
	}

	get component_to_show() {
		const { league } = this.props;
		const { show_team, fa_trade, team_trade, is_fa_trade, is_team_trade } = this.state;
		if (is_team_trade) {
			return (
				<TeamToTeamTrade
					league={league}
					cancelTradeRequest={this.cancelTradeRequest}
					team_trade={team_trade}
				/>
			);
		}
		if (show_team && is_fa_trade) {
			return (
				<TeamForTrade
					fa={fa_trade}
					removeOutPlayer={this.removeOutPlayer}
					addOutTrade={this.addOutTrade}
				/>
			);
		}

		return (
			<PlayersTable
				league={league}
				addOutTrade={this.addOutTrade}
				addInTrade={this.addInTrade}
				cancelTradeRequest={this.cancelTradeRequest}
				removeOutPlayer={this.removeOutPlayer}
				removeInPlayer={this.removeInPlayer}
				addOwnerFilter
			/>
		);
	}
	reFetchData() {
		this.cancelTradeRequest();

		const {
			fetchTeams,
			fetchWaiverList,
			fetchWaiverFree,
			match: { params: { league_id } },
		} = this.props;
		fetchTeams({
			league_id: league_id,
			details: 1
		});

		fetchWaiverList({
			league_id
		});
		fetchWaiverFree({
			league_id
		});
	}

	addOutTrade({ id, is_user_team, owner, waiver, is_fa }) {
		const { fa_trade, is_fa_trade, filters } = this.state;

		if (is_fa_trade) {
			this.setState({
				fa_trade: {
					...fa_trade,
					old_player_id: id
				},
				is_fa_trade: true
			});
		}
		if (!owner) {
			this.setState({
				fa_trade: {
					...fa_trade,
					old_player_id: id
				},
				is_fa_trade: true
			});
		}
		else if (is_user_team && !is_fa) {
			const { team_trade } = this.state;
			const { old_player_id } = team_trade;
			this.setState({
				team_trade: {
					...team_trade,
					old_player_id: _.xor(old_player_id, [id]),
					owner_id: owner.id
				},
				is_team_trade: true
			});
		}
		if (is_user_team && is_fa) {
			this.setState({
				fa_trade: {
					...fa_trade,
					old_player_id: id
				},
				filters: {
					...filters,
					by_fa: true,
				},
				is_fa_trade: true
			});
		}
	}
	addInTrade({ id, is_user_team, owner, waiver }) {
		const { fa_trade } = this.state;
		if (!owner) {
			this.setState({
				fa_trade: {
					...fa_trade,
					new_player_id: id
				},
				is_fa_trade: true,
				show_team: true
			});
		}
		if (owner) {
			const { team_trade } = this.state;
			const { new_player_id } = team_trade;
			this.setState({
				team_trade: {
					...team_trade,
					new_player_id: _.xor(new_player_id, [id]),
					owner_id: owner.id
				},
				is_team_trade: true
			});
		}
	}
	cancelTradeRequest() {
		const { filters } = this.state;

		this.setState({
			fa_trade: {
				new_player_id: 0,
				old_player_id: 0
			},
			team_trade: {
				new_player_id: [],
				old_player_id: []
			},
			is_fa_trade: false,
			is_team_trade: false,
			show_team: false,
			filters: {
				...filters,
				by_fa: false
			}
		});
	}
	removeOutPlayer({ id }) {
		const { is_fa_trade, is_team_trade, fa_trade, team_trade } = this.state;
		if (is_fa_trade) {
			const is_fa_trade_remove = fa_trade.new_player_id === 0;

			this.setState({
				fa_trade: {
					...fa_trade,
					old_player_id: 0
				},
				is_fa_trade: !is_fa_trade_remove
			});
		}
		if (is_team_trade) {
			const { new_player_id, old_player_id } = team_trade;
			const is_trade_remove = _.size(new_player_id.filter(id => id !== 0)) === 0;
			this.setState({
				team_trade: {
					...team_trade,
					old_player_id: _.xor(old_player_id, [id])
				},
				is_team_trade: !is_trade_remove

			});
		}
	}
	removeInPlayer({ id }) {
		const { is_fa_trade, is_team_trade, fa_trade, team_trade } = this.state;
		if (is_fa_trade) {
			const is_fa_trade_remove = fa_trade.old_player_id === 0;
			this.setState({
				fa_trade: {
					...fa_trade,
					new_player_id: 0
				},
				is_fa_trade: !is_fa_trade_remove
			});

		}
		if (is_team_trade) {
			const { new_player_id, old_player_id } = team_trade;
			const is_trade_remove = _.size(old_player_id.filter(id => id !== 0)) === 0;

			this.setState({
				team_trade: {
					...team_trade,
					new_player_id: _.xor(new_player_id, [id])
				},
				is_team_trade: !is_trade_remove
			});
		}
	}

	scrollToComponent() {
		if (this.ref.current) {
			this.ref.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
				inline: 'center',
			});
		}
	}

	render() {
		const { league, user_team, has_assistant_coach } = this.props;
		const{
			fa_trade,
			is_fa_trade
		} = this.state;

		if (is_fa_trade) {
			this.scrollToComponent();
		}

		const json_files_to_fetch = ['players', 'rounds', 'squads', 'venues'];

		if(has_assistant_coach) {
			json_files_to_fetch.push('coach_players');
		}

		return (
			<div ref={this.ref}>
				<JsonFetcher fetch={json_files_to_fetch} interval={60000} />
				<TopBar league={league} />
				<AdsContainer>
					<Ad
						adUnit='myleagues/myteam'
						size='top'
					/>
				</AdsContainer>
				<OneColumnLayout width='1060px'>

					{is_fa_trade? (
						<PlayersOnTrade
							league={league}
							fa={fa_trade}
							type="fa"
							reFetchData={this.reFetchData}
							cancelRequest={this.cancelTradeRequest}
							removeOutPlayer={this.removeOutPlayer}
							removeInPlayer={this.removeInPlayer}
							user_team={user_team}
						/>
					) : null}
					{this.component_to_show}

				</OneColumnLayout>
				<AdsContainer>
					<Ad
						adUnit='myleagues/myteam'
						size='bottom'
					/>
				</AdsContainer>
			</div>
		);
	}
}

const mapStateToProps = (state: TRootStore, props) => ({
	players: selectors.players.getExtendedPlayersArrayWithOwners(state, props),
	is_pending_players: state.players.is_pending,
	squads: selectors.squads.getSquads(state),
	positions: selectors.positions.getPositionsArray(state),
	playerStatuses: state.playerStatuses,
	league: selectors.leagueDraft.getLeague(state, props),
	teams_by_id: state.leagues.teams_by_id,
	actual_round: selectors.rounds.getActualRound(state),
	has_assistant_coach: Boolean(selectors.getUser(state).assistant_coach),
	coach_stats_list: state.coachStatsList,
	coach_players_by_id: state.players.coach_by_id,
	user_team: state.teamsDraft.user_team,
});

const mapDispatchToProps = {
	showDraftLeague: actions.leagueDraft.showDraftLeague,
	fetchTeams: actions.leagueDraft.showDraftOrder,
	fetchCoachPlayers: actions.players.fetchCoachPlayers,
	fetchWaiverList: actions.leagueDraft.fetchWaiverList,
	fetchWaiverFree: actions.leagueDraft.fetchWaiverFree,
	fetchTeam: actions.teamsDraft.fetchTeam,
	fetchRounds: actions.rounds.fetchRounds,
};
export const Players = withRouter(
	connect(
		mapStateToProps,
		mapDispatchToProps,
	)(PlayersComponent)
);

export default Players;


// WEBPACK FOOTER //
// ./src/pages/league/Players.js